import type React from 'react';
import { useTranslation } from 'react-i18next';
import { Chip, Typography } from '@heroui/react';
import { Icon } from '@iconify/react';
import { getNodeTestProps } from '@/utils';
import type { DashboardLogic } from './useDashboard';

/**
 * Admin dashboard (render-only). Rendered inside the app shell, so it carries
 * no top bar of its own — just the page heading and a grid of management
 * feature tiles. Each tile routes to that feature's page (a "coming soon" stub
 * until built); the same features appear in the shell sidebar.
 */
export const DashboardView: React.FC<DashboardLogic> = ({
    features,
    openFeature,
}) => {
    const { t } = useTranslation('pages');

    return (
        <div
            className="flex w-full flex-col gap-6"
            {...getNodeTestProps('dashboard-page')}
        >
            <div className="flex flex-col gap-1">
                <Typography type="h4" weight="bold">
                    {t('dashboard.title')}
                </Typography>
                <Typography color="muted" type="body-sm">
                    {t('dashboard.subtitle')}
                </Typography>
            </div>

            <div
                className="grid gap-3"
                {...getNodeTestProps('dashboard-action-grid')}
                style={{
                    gridTemplateColumns: 'repeat(auto-fill,minmax(200px,1fr))',
                }}
            >
                {features.map((feature) => {
                    const label = t(`seller.actions.${feature.slug}`);
                    return (
                        <button
                            aria-label={label}
                            className="group flex cursor-pointer flex-col items-start gap-3 rounded-2xl border border-border bg-surface p-5 text-left transition hover:border-accent/40 hover:bg-surface-secondary"
                            key={feature.slug}
                            {...getNodeTestProps(
                                `dashboard-action-${feature.slug}`,
                            )}
                            onClick={() => openFeature(feature.path)}
                            type="button"
                        >
                            <div className="flex size-10 items-center justify-center rounded-xl bg-surface-secondary text-accent">
                                <Icon className="size-5" icon={feature.icon} />
                            </div>
                            <Typography weight="semibold">{label}</Typography>
                            <Chip color="warning" size="sm" variant="soft">
                                {t('dashboard.comingSoon')}
                            </Chip>
                        </button>
                    );
                })}
            </div>
        </div>
    );
};
